import _once from 'lodash/once';

/* eslint-disable tsdoc/syntax -- need to escape otherwise, making it unreadable in file */
/**
 * Regular expressions for matching variable expressions in double curly braces {{ ... }}
 *
 * STRINGIFIED_EXPRESSION matches dot notation and array access patterns:
 * - Matches:
 *   - {{ user.name }}
 *   - {{ items[0] }}
 *   - {{ data['key'] }}
 *   - {{ deeply.nested.array[0].property }}
 *   - {{ data['key's'] }} (quotes within quotes)
 *   - {{ data["key\"s"] }} (escaped quotes)
 *   - {{ user\\.name }} (escaped dots)
 * - Does not match:
 *   - {{ user name }} (spaces not allowed in path)
 *   - {{ func() }} (function calls not supported)
 *   - {{ obj{} }} (nested braces not allowed)
 *   - {{}} (empty expressions not allowed)
 *   - {{  }} (whitespace-only expressions not allowed)
 *
 */
// Base identifier pattern for variable names
const IDENTIFIER_PATTERN = '[a-zA-Z0-9_$=:.-]+';

// Pattern for dot notation access (e.g., .propertyName)
// Also allows escaped dots (\.)
const DOT_NOTATION_PATTERN = '(?:\\\\\\.[a-zA-Z0-9_$=:.-]*|\\.[a-zA-Z0-9_$=:.-]+)';

// Pattern for array/bracket notation access with special handling for quotes
// Allows any characters including escape sequences and quotes inside quotes
// Pattern for single-quoted string in bracket notation
const SINGLE_QUOTED_PATTERN = "'(?:[^'\\\\]|\\\\.|'(?!\\]))*'";

// Pattern for double-quoted string in bracket notation
const DOUBLE_QUOTED_PATTERN = '"(?:[^"\\\\]|\\\\.|"(?!\\]))*"';

// Pattern for numeric index in bracket notation
const NUMERIC_INDEX_PATTERN = '\\d+';

// Combined pattern for array/bracket notation access
const BRACKET_NOTATION_PATTERN = `\\[(?:${SINGLE_QUOTED_PATTERN}|${DOUBLE_QUOTED_PATTERN}|${NUMERIC_INDEX_PATTERN})\\]`;

// Pattern for property access (either dot notation or bracket notation)
const PROPERTY_ACCESS_PATTERN = `(?:${DOT_NOTATION_PATTERN}|${BRACKET_NOTATION_PATTERN})`;

// Complete expression pattern for variables with optional property access
export const VARIABLE_PATH_PATTERN = `${IDENTIFIER_PATTERN}(?:${PROPERTY_ACCESS_PATTERN})*`;

// Full pattern for expressions in double curly braces
// Whenever any change is made to this regex, change it packages/block-editor/src/extensions/TransformTextToVariableMention.ts
export const STRINGIFIED_EXPRESSION = `{{\\s*(?<id>${VARIABLE_PATH_PATTERN})\\s*}}`;
export const EXPRESSION_REGEX = new RegExp(STRINGIFIED_EXPRESSION, 'g');

export interface ExpressionGroups {
  /** The main content between \{\{ and \}\} delimiters, containing the actual expression to be evaluated */
  id: string;
  /** Any whitespace characters that appear after the opening \{\{ and before the expression */
  leadingSpaces: string;
  /** Any whitespace characters that appear after the expression and before the closing \}\} */
  trailingSpaces: string;
}

// Same as above, but here we allow spaces in the expression (expression can contain spaces but not at the start or end)
export const TEXT_EXPRESSION_WITH_GROUPS =
  '(?<!{){{(?<leadingSpaces>\\s*)(?<id>(?!\\s)[^{}]+?(?<!\\s))(?<trailingSpaces>\\s*)}}(?!})';

export const getTextExpressionWithGroupsRegex = _once(
  () => new RegExp(TEXT_EXPRESSION_WITH_GROUPS, 'g'),
);

/*
 * Same as above, but here we allow spaces in the expression (expression can contain spaces but not at the start or end)
 * TEXT_EXPRESSION is more permissive and allows spaces within the expression:
 * - Matches:
 *   - {{ some text here }}
 *   - {{ User Name }}
 * - Does not match:
 *   - {{ text}} (requires space after opening braces)
 *   - {{text }} (requires space before closing braces)
 *   - {{ nested {{ braces }} }} (nested braces not allowed)
 */
export const TEXT_EXPRESSION = '{{\\s*(?<id>(?!\\s)[^{}]+?(?<!\\s))\\s*}}';

export const getTextExpressionRegex = _once(() => new RegExp(TEXT_EXPRESSION, 'g'));
